import { NetworkResult, SubnetInfo } from '../types/network';

export class NetworkCalculator {
  static ipToArray(ip: string): number[] {
    // Просто разбиваем по точкам и преобразуем в числа
    return ip.split('.').map(part => {
      const num = Number(part);
      return isNaN(num) ? 0 : Math.max(0, Math.min(255, num));
    }).slice(0, 4); // Берем только первые 4 октета
  }

  static arrayToIp(arr: number[]): string {
    return arr.join('.');
  }

  static applyMask(ipArray: number[], maskArray: number[]): number[] {
    return ipArray.map((octet, i) => octet & maskArray[i]);
  }

  static getBroadcastAddress(networkArray: number[], maskArray: number[]): number[] {
    return networkArray.map((octet, i) => octet | (255 ^ maskArray[i]));
  }

  // Проверяем принадлежность к частным сетям RFC1918
static isRFC1918(ip: string): boolean {
  const ipArray = this.ipToArray(ip);
  
  // 10.0.0.0/8
  if (ipArray[0] === 10) return true;
  
  // 172.16.0.0/12
  if (ipArray[0] === 172 && ipArray[1] >= 16 && ipArray[1] <= 31) return true;
  
  // 192.168.0.0/16
  if (ipArray[0] === 192 && ipArray[1] === 168) return true;
  
  return false;
}

// Проверяем специальные/зарезервированные сети
static isSpecialNetwork(ip: string, mask: string): boolean {
  const ipArray = this.ipToArray(ip);
  const maskArray = this.ipToArray(mask);
  const networkArray = this.applyMask(ipArray, maskArray);
  
  // 0.0.0.0/8 - "This" network
  if (networkArray[0] === 0) return true;
  
  // 127.0.0.0/8 - Loopback
  if (networkArray[0] === 127) return true;
  
  // 169.254.0.0/16 - Link-local
  if (networkArray[0] === 169 && networkArray[1] === 254) return true;
  
  // 224.0.0.0/4 - Multicast
  if (networkArray[0] >= 224 && networkArray[0] <= 239) return true;
  
  // 240.0.0.0/4 - Reserved
  if (networkArray[0] >= 240) return true;
  
  return false;
}

// Получаем предупреждения для сети
static getNetworkWarnings(ip: string, mask: string): string[] {
  const warnings: string[] = [];
  const ipArray = this.ipToArray(ip);
  const maskArray = this.ipToArray(mask);
  const networkArray = this.applyMask(ipArray, maskArray);
  const network = this.arrayToIp(networkArray);
  
  // Проверка RFC1918
  if (!this.isRFC1918(network)) {
    warnings.push('Сеть не относится к частным адресам RFC1918');
  }
  
  // Проверка специальных сетей
  if (this.isSpecialNetwork(ip, mask)) {
    if (networkArray[0] === 127) {
      warnings.push('Это loopback сеть (127.0.0.0/8)');
    } else if (networkArray[0] === 169 && networkArray[1] === 254) {
      warnings.push('Это link-local сеть (169.254.0.0/16)');
    } else if (networkArray[0] >= 224 && networkArray[0] <= 239) {
      warnings.push('Это multicast сеть (224.0.0.0/4)');
    } else if (networkArray[0] >= 240) {
      warnings.push('Это зарезервированная сеть (240.0.0.0/4)');
    } else if (networkArray[0] === 0) {
      warnings.push('Это "this" сеть (0.0.0.0/8)');
    }
  }
  
  // Проверка на слишком большие сети (маска /13 и больше)
  const cidr = this.getCidrFromMask(mask);
    if (cidr < 16) {
      warnings.push(`Маска /${cidr} создает очень большую сеть из доступных хостов: ${Math.pow(2, 32 - cidr)}`);
    }
    
    // Проверка на слишком маленькие сети (маска /30 и больше)
    if (cidr > 29) {
      warnings.push(`Маска /${cidr} создает очень маленькую сеть из доступных хостов: ${Math.pow(2, 32 - cidr) - 2}`);
    }
    
    return warnings;
  }

  static calculateSubnets(mainNetwork: string, newCidr: number): SubnetInfo[] {
    const networkArray = this.ipToArray(mainNetwork);
    const subnets: SubnetInfo[] = [];
    const subnetSize = Math.pow(2, 32 - newCidr);
    
    let currentNetwork = networkArray;
    
    for (let i = 0; i < Math.pow(2, newCidr - this.getCidrFromMask('255.255.255.0')); i++) {
      const subnetNetwork = [...currentNetwork];
      const broadcast = this.getBroadcastAddress(subnetNetwork, this.cidrToMask(newCidr));
      
      subnets.push({
        subnet: `${this.arrayToIp(subnetNetwork)}/${newCidr}`,
        networkAddress: this.arrayToIp(subnetNetwork),
        broadcastAddress: this.arrayToIp(broadcast),
        usableRange: `${this.arrayToIp(subnetNetwork.map((octet, idx) => idx === 3 ? octet + 1 : octet))} - ${this.arrayToIp(broadcast.map((octet, idx) => idx === 3 ? octet - 1 : octet))}`,
        hosts: Math.pow(2, 32 - newCidr) - 2
      });
      
      // Move to next subnet
      let carry = subnetSize;
      for (let j = 3; j >= 0; j--) {
        currentNetwork[j] += carry;
        carry = Math.floor(currentNetwork[j] / 256);
        currentNetwork[j] %= 256;
      }
    }
    
    return subnets;
  }

  static cidrToMask(cidr: number): number[] {
    let mask = [];
    for (let i = 0; i < 4; i++) {
      if (cidr >= (i + 1) * 8) {
        mask.push(255);
      } else if (cidr <= i * 8) {
        mask.push(0);
      } else {
        const bits = cidr - i * 8;
        mask.push(256 - Math.pow(2, 8 - bits));
      }
    }
    return mask;
  }

  static isValidMask(mask: string): boolean {
    try {
      const maskArray = this.ipToArray(mask);
      
      // Проверяем что все октеты числа
      if (maskArray.some(isNaN)) return false;
      
      // Проверяем диапазон значений
      if (maskArray.some(octet => octet < 0 || octet > 255)) return false;
      
      const maskBinary = maskArray.map(octet => 
        octet.toString(2).padStart(8, '0')
      ).join('');
      
      return /^1+0+$/.test(maskBinary);
    } catch {
      return false;
    }
  }

  static isValidIp(ip: string): boolean {
    try {
      const ipArray = this.ipToArray(ip);
      
      // Проверяем что все октеты числа
      if (ipArray.some(isNaN)) return false;
      
      // Проверяем диапазон значений
      if (ipArray.some(octet => octet < 0 || octet > 255)) return false;
      
      return ipArray.length === 4;
    } catch {
      return false;
    }
  }
  static getCidrFromMask(mask: string): number {
    if (!this.isValidMask(mask)) {
      throw new Error('Неверная маска подсети');
    }
    
    const maskArray = this.ipToArray(mask);
    return maskArray.reduce((acc, octet) => 
      acc + octet.toString(2).split('1').length - 1, 0
    );
  }

  static calculateAll(ipAddress: string, subnetMask: string): NetworkResult {
    // Чистим входные данные от CIDR
    const cleanIp = ipAddress.split('/')[0];
    const cleanMask = subnetMask.split('/')[0];
    
    const ipArray = this.ipToArray(cleanIp);
    const maskArray = this.ipToArray(cleanMask);

    if (ipArray.length !== 4 || maskArray.length !== 4) {
      throw new Error('Неверный формат IP или маски');
    }

    // Проверяем что все октеты - валидные числа
    if (ipArray.some(octet => isNaN(octet) || octet < 0 || octet > 255) ||
        maskArray.some(octet => isNaN(octet) || octet < 0 || octet > 255)) {
      throw new Error('IP и маска должны содержать числа от 0 до 255');
    }

    // Проверяем что маска валидна (последовательность 1 followed by 0)
    const maskBinary = maskArray.map(octet => 
        octet.toString(2).padStart(8, '0')
    ).join('');
    
    if (!/^1+0+$/.test(maskBinary)) {
        throw new Error('Неверная маска подсети');
    }

    // Вычисляем адрес сети (IP AND Mask)
    const networkAddress = this.applyMask(ipArray, maskArray);
    
    // Вычисляем широковещательный адрес (Network OR Inverse Mask)
    const broadcastAddress = this.getBroadcastAddress(networkAddress, maskArray);
    
    // Вычисляем количество бит хоста
    const hostBits = maskArray.reduce((acc, octet) => {
        return acc + (255 ^ octet).toString(2).split('1').length - 1;
    }, 0);
    
    // Вычисляем общее количество хостов
    const totalHosts = Math.pow(2, hostBits);
    
    // Вычисляем доступное количество хостов (исключаем network и broadcast)
    const usableHosts = Math.max(0, totalHosts - 2);

    // Вычисляем wildcard маску (инверсия маски)
    const wildcardMask = maskArray.map(octet => 255 - octet);

    // Определяем класс сети
    let networkClass = '';
    const firstOctet = ipArray[0];
    if (firstOctet >= 1 && firstOctet <= 126) {
        networkClass = 'A';
    } else if (firstOctet >= 128 && firstOctet <= 191) {
        networkClass = 'B';
    } else if (firstOctet >= 192 && firstOctet <= 223) {
        networkClass = 'C';
    } else if (firstOctet >= 224 && firstOctet <= 239) {
        networkClass = 'D (Multicast)';
    } else if (firstOctet >= 240 && firstOctet <= 255) {
        networkClass = 'E (Reserved)';
    } else {
        networkClass = 'Unknown';
    }

    // Вычисляем CIDR
    const cidr = this.getCidrFromMask(subnetMask);

    // Возвращаем результат
    return {
        networkAddress: this.arrayToIp(networkAddress),
        broadcastAddress: this.arrayToIp(broadcastAddress),
        firstUsable: this.arrayToIp(networkAddress.map((octet, i) => 
            i === 3 ? octet + 1 : octet
        )),
        lastUsable: this.arrayToIp(broadcastAddress.map((octet, i) => 
            i === 3 ? octet - 1 : octet
        )),
        totalHosts,
        usableHosts,
        wildcardMask: this.arrayToIp(wildcardMask),
        networkClass,
        cidr,
        ipAddress,
        subnetMask,
        calculationDate: new Date()
    };
  }
}